/**
 * Busca de CEP (ADR-0071) — o backend consulta BrasilAPI com fallback no
 * ViaCEP e guarda em cache; a tela nunca fala direto com os provedores.
 *
 * Endpoints:
 *   GET /api/cep/{cep}                             autenticado (telas internas)
 *   GET /api/public/formularios/{slug}/cep/{cep}   sem login (pagina /f/{slug})
 *
 * As duas funcoes tem o mesmo contrato, para o `EnderecoFields` receber
 * qualquer uma como `buscar`:
 *   - encontrou          -> {cep, logradouro, bairro, cidade, estado}
 *   - nao encontrou (404) -> null  (a tela pede o endereco a mao)
 *   - qualquer outra falha -> lanca `ErroBuscaCep` com `codigo`:
 *       "invalido"     CEP sem 8 digitos (400, ou barrado antes da request)
 *       "indisponivel" provedores fora (503), limite (429), rede, formulario
 *                      inativo — em todos a pessoa preenche a mao
 *
 * Sem toast: a falha aparece como estado ao lado do campo de CEP, entao a
 * request interna vai com `silentError` para o AuthContext nao repetir a
 * mesma mensagem num toast.
 */

import { normalizarCep } from "../utils/endereco";
import { publicFetch } from "./formularios";

export const CEP_INVALIDO = "invalido";
export const BUSCA_CEP_INDISPONIVEL = "indisponivel";

const HTTP_CEP_INVALIDO = 400;
const HTTP_NAO_ENCONTRADO = 404;

// A rota publica tambem responde 404 quando o formulario saiu do ar. Isso
// nao e "CEP nao encontrado": a busca e que ficou indisponivel.
const MSG_FORMULARIO_NAO_ENCONTRADO = "Formulário não encontrado.";

export class ErroBuscaCep extends Error {
  constructor(codigo, mensagem, causa) {
    super(mensagem);
    this.name = "ErroBuscaCep";
    this.codigo = codigo;
    this.causa = causa;
  }
}

function cepParaRota(cep) {
  const digitos = normalizarCep(cep);
  if (!digitos) {
    throw new ErroBuscaCep(CEP_INVALIDO, "CEP inválido: informe os 8 dígitos.");
  }
  return digitos;
}

function traduzirFalha(err, { rotaPublica = false } = {}) {
  const status = err?.status;
  const detalhe = err?.payload?.detalhe ?? err?.body?.detalhe;
  if (status === HTTP_NAO_ENCONTRADO && !(rotaPublica && detalhe === MSG_FORMULARIO_NAO_ENCONTRADO)) {
    return null;
  }
  if (status === HTTP_CEP_INVALIDO) {
    throw new ErroBuscaCep(CEP_INVALIDO, err.message, err);
  }
  throw new ErroBuscaCep(
    BUSCA_CEP_INDISPONIVEL,
    "Busca de CEP indisponível no momento. Preencha o endereço à mão.",
    err
  );
}

/** Busca autenticada — `request` e o do `useAuth()`. */
export async function buscarCep(request, cep) {
  const digitos = cepParaRota(cep);
  try {
    return await request(`/api/cep/${digitos}`, { silentError: true });
  } catch (err) {
    return traduzirFalha(err);
  }
}

/** Busca da pagina publica, presa ao formulario ativo `slug`. */
export async function buscarCepPublico(slug, cep) {
  const digitos = cepParaRota(cep);
  try {
    return await publicFetch(
      `/api/public/formularios/${encodeURIComponent(slug)}/cep/${digitos}`
    );
  } catch (err) {
    return traduzirFalha(err, { rotaPublica: true });
  }
}
